<template>
  <div class="assign-row">
    <span v-if="showLabel" class="row-label">{{ $t(labelI18nKey) }}</span>
    <div ref="assignRowRef" class="row-content">
      <el-dropdown
        ref="dropdown"
        trigger="click"
        placement="bottom-start"
        :disabled="disabled"
        :hide-on-click="false"
        @visible-change="handlePopoverVisibleChange"
      >
        <div class="add-trigger" :class="{ 'is-disabled': disabled }" @click="addBtnClick">
          <i class="iconfont iconAdd"></i>
        </div>
        <el-dropdown-menu slot="dropdown" class="assign-dropdown-menu" :style="{ width: dropdownWidth}">
          <div class="dropdown-content">
            <el-input
              v-model="optionSearchKeyword"
              clearable
              size="small"
              class="search-input"
            />
            <div class="selectable-list">
              <template v-if="filteredOptionList.length">
                <div
                  v-for="item in filteredOptionList"
                  class="list-item"
                  :key="item.id"
                  @click="toggleOption(item)"
                >
                  <el-checkbox :value="isSelectedOption(item.id)" :disabled="disabled" @click.native.prevent />
                  <span class="item-name">{{ item.name }}</span>
                </div>
              </template>
              <el-empty v-else :description="$t('admin.talent.label.noData')" :image-size="60" />
            </div>
          </div>
        </el-dropdown-menu>
      </el-dropdown>
      <div class="selected-tags">
        <div
          v-for="item in selectedOptionItems"
          class="selected-tag"
          :key="item.id"
        >
          <span class="tag-label">{{ item.name }}</span>
          <i v-if="!disabled" class="el-icon-close remove-btn" @click="removeOption(item.id)"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  props: {
    showLabel: {
      type: Boolean,
      default: true
    },
    labelI18nKey: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    optionList: {
      type: Array,
      default: () => []
    },
    selectIds: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      optionSearchKeyword: null,
      dropdownWidth: null
    }
  },
  computed: {
    hasSelectedIds: {
      get () {
        return this.selectIds
      },
      set (val) {
        this.$emit('update:selectIds', val)
      }
    },
    filteredOptionList () {
      if (!this.optionSearchKeyword) {
        return this.optionList
      }
      const keyword = this.optionSearchKeyword.toLowerCase()
      return this.optionList.filter(item =>
        (item.name || '').toLowerCase().includes(keyword)
      )
    },
    selectedOptionItems () {
      return this.optionList.filter(item => this.hasSelectedIds.includes(item.id))
    }
  },
  methods: {
    handlePopoverVisibleChange () {
      this.optionSearchKeyword = null
    },
    addBtnClick () {
      this.dropdownWidth = `${this.$refs.assignRowRef.getBoundingClientRect().width}px`
    },
    isSelectedOption (id) {
      return this.hasSelectedIds.includes(id)
    },
    toggleOption (item) {
      if (this.disabled) {
        return
      }
      if (this.isSelectedOption(item.id)) {
        this.removeOption(item.id)
      } else {
        this.hasSelectedIds = [...this.hasSelectedIds, item.id]
        this.updateDropdownPopper()
      }
    },
    removeOption (id) {
      this.hasSelectedIds = this.hasSelectedIds.filter(i => i !== id)
      this.updateDropdownPopper()
    },
    updateDropdownPopper () {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          this.$refs.dropdown?.broadcast('ElDropdownMenu', 'updatePopper')
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.assign-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  .row-label {
    width: 120px;
    font-size: 14px;
    color: #606266;
    line-height: 28px;
    flex-shrink: 0;
    text-align: right;
    margin-right: 20px;
  }

  .row-content {
    flex: 1;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
    overflow-y: auto;
    max-height: 138px;

    .add-trigger {
      margin-top: 3px;
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      border-radius: 50%;
      transition: background-color 0.2s ease, color 0.2s ease;

      i {
        color: #0096cc;
        font-size: 18px;
        line-height: 1;
      }

      &:hover {
        background-color: #f0f9ff;
      }

      &.is-disabled {
        border-color: #c0c4cc;
        cursor: not-allowed;

        i {
          color: #c0c4cc;
        }

        &:hover {
          background-color: transparent;
        }
      }
    }

    .selected-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      min-height: 22px;

      .selected-tag {
        min-height: 28px;
        padding: 4px 10px;
        border: 1px solid #dddedf;
        border-radius: 14px;
        display: flex;
        align-items: center;
        background-color: #fff;
        line-height: 1.4;

        .tag-label {
          font-size: 13px;
          color: #303133;
        }

        .remove-btn {
          margin-left: 8px;
          color: #909399;
          font-size: 12px;
          cursor: pointer;

          &:hover {
            color: #f56c6c;
          }
        }
      }
    }
  }
}

</style>

<style lang="scss">
.assign-dropdown-menu {
  min-width: 290px;
  width: 300px;
  padding: 0 !important;

  .dropdown-content {
    display: flex;
    flex-direction: column;
    max-height: 292px;

    .search-input {
      flex-shrink: 0;
      padding: 0;

      .el-input__inner {
        height: 32px;
        padding: 0 30px 0 12px;
        border-radius: 4px;
        line-height: 32px;
      }

      .el-input__suffix {
        right: 10px;
        display: flex;
        align-items: center;
      }

      .el-input__suffix-inner {
        display: flex;
        align-items: center;
      }

      .el-input__icon {
        width: auto;
        line-height: 32px;
      }
    }

    .selectable-list {
      flex: 1;
      overflow-y: auto;
      padding: 4px 0 8px;
      border-top: 1px solid #f0f2f5;

      .list-item {
        padding: 9px 12px;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
          background-color: #f5f7fa;
        }

        .item-name {
          margin-left: 8px;
          font-size: 14px;
          color: #303133;
          line-height: 20px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
  .popper__arrow {
    display: none !important;
  }
}
</style>
